// Capability detection for legacy/limited browsers.
// Keep this fast, side-effect free, and cached to avoid repeated checks.

let cachedCapabilities: Capabilities | null = null;

export type Capabilities = {
  isIOS: boolean;
  isLegacyIOS: boolean;
  hasCrypto: boolean;
  hasRandomUUID: boolean;
  hasGetRandomValues: boolean;
  hasIndexedDB: boolean;
  hasAudioContext: boolean;
  hasAudioWorklet: boolean;
  hasCacheStorage: boolean;
  hasServiceWorker: boolean;
  offlineFirstSafe: boolean;
  supportsStems: boolean;
  forceLite: boolean;
  limitedReason: string | null;
};

export const getCapabilities = (): Capabilities => {
  if (cachedCapabilities) return cachedCapabilities;

  const hasWindow = typeof window !== "undefined";
  const nav = typeof navigator !== "undefined" ? navigator : null;
  const userAgent = nav?.userAgent || "";
  const isIOS = /iP(ad|hone|od)/.test(userAgent);
  const isIOS12 = /OS 12[_\d]*\s/.test(userAgent);

  const hasCrypto = typeof crypto !== "undefined";
  const hasRandomUUID = hasCrypto && typeof crypto.randomUUID === "function";
  const hasGetRandomValues = hasCrypto && typeof crypto.getRandomValues === "function";
  const isSecureContext = hasWindow && typeof window.isSecureContext === "boolean" ? window.isSecureContext : false;
  const hasIndexedDB = (() => {
    try {
      return Boolean(hasWindow && window.indexedDB);
    } catch (_) {
      return false;
    }
  })();

  const hasAudioContext = Boolean(hasWindow && (window.AudioContext || window.webkitAudioContext));
  const hasAudioWorklet = Boolean(hasWindow && window.AudioWorkletNode);
  const hasCacheStorage = Boolean(hasWindow && "caches" in window);
  const hasServiceWorker = Boolean(nav && "serviceWorker" in nav);
  const offlineFirstSafe = hasIndexedDB && hasCacheStorage && hasServiceWorker;

  // Legacy iOS Safari often lacks crypto.randomUUID and is fragile with IndexedDB.
  // For HTTP dev/testing, allow a narrow UA fallback for iOS 12 to avoid false positives on modern iOS.
  const isLegacyIOS = isIOS && !hasRandomUUID && (isSecureContext || isIOS12);

  // Force Lite mode on legacy iOS or when IndexedDB is unavailable.
  const forceLite = isLegacyIOS || !hasIndexedDB;
  const supportsStems = hasAudioContext && hasIndexedDB;
  const limitedReason = forceLite
    ? isLegacyIOS
      ? "Legacy iOS"
      : !hasIndexedDB
        ? "IndexedDB unavailable"
        : "Limited device"
    : null;

  cachedCapabilities = {
    isIOS,
    isLegacyIOS,
    hasCrypto,
    hasRandomUUID,
    hasGetRandomValues,
    hasIndexedDB,
    hasAudioContext,
    hasAudioWorklet,
    hasCacheStorage,
    hasServiceWorker,
    offlineFirstSafe,
    supportsStems,
    forceLite,
    limitedReason,
  };

  return cachedCapabilities;
};

export const safeRandomUUID = (): string => {
  const caps = getCapabilities();
  if (caps.hasRandomUUID && typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  if (caps.hasGetRandomValues && typeof crypto !== "undefined" && crypto.getRandomValues) {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant
    const hex = Array.from(bytes).map((b) => b.toString(16).padStart(2, "0"));
    return `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-${hex.slice(6, 8).join("")}-${hex.slice(8, 10).join("")}-${hex.slice(10, 16).join("")}`;
  }
  return `uuid_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};
